import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import ListGroup from 'react-bootstrap/ListGroup';
import { Button } from '@janus.team/janus-particles';
import Card from 'react-bootstrap/Card';
import { ExternalLink } from 'common/ExternalLink';
import { Route } from 'react-router-dom';
import { SmartGCPRequestAccessButton, PassportButton } from 'projects/index';
import { ProjectLinkedState } from 'projects/project-linked-state';
import { userHasChangeServiceLevelPermission } from 'projects/utils';
import { useQueryGetProjectsFromJanus, useQueryGetSystemsAccount } from 'hooks/queries';
import { Loading } from 'components/loading';
import { useParsedQueryString, useUpdatedTo } from 'projects/project-selector/hooks';
import { useProductModelContext } from 'customer/product-model-context';
import { baseUrl } from 'config';
import { UnlinkModalButton } from 'projects/unlink-modal';
import { ServiceLevel } from './service-level';
import { ManageServicesDrawer } from '../manageserviceblocks';

const Pair = ({ label, value }) => {
  const id = `project-details-key-${_.kebabCase(label)}`;
  return (
    <>
      <strong id={id}>{label}</strong>
      <span aria-labelledby={id}>{value}</span>
    </>
  );
};

const ManageServicesButton = ({ project }) => {
  const [showManageServices, setShowManageServices] = React.useState(false);
  const { products } = useProductModelContext();
  const hasEnoughPermissions = userHasChangeServiceLevelPermission();
  const isProjectLinked = project?.linkedState === 'LINKED';
  if (!isProjectLinked || !hasEnoughPermissions || _.isEmpty(products)) {
    return null;
  }
  return (
    <>
      <Button onClick={() => setShowManageServices(!showManageServices)}>
        <i className="fa fa-gear" />
        {' '}
        Manage services
      </Button>
      <ManageServicesDrawer cloud="gcp" productAccount={project} showManageServices={showManageServices} setShowManageServices={setShowManageServices} products={products} />
    </>
  );
};

const ServiceLevelPair = ({ label, value }) => {
  const id = `project-details-key-${_.kebabCase(label)}`;
  return (
    <>
      <strong>Service Level</strong>
      <span aria-label={id} className="d-flex flex-wrap align-items-center gap-3">{value}</span>
    </>
  );
};

export const SystemsAccountPair = ({ projectId }) => {
  const systemsAccount = useQueryGetSystemsAccount(projectId);
  const buildPair = (value) => (
    <Pair label="Systems Account" value={value} />
  );

  if (systemsAccount.isLoading) {
    return buildPair(<Loading message=" " />);
  }

  const customerAccount = _.first(_.get(systemsAccount, 'data.customerAccount'));
  const awsAccountName = _.get(customerAccount, 'name');
  const awsAccountId = _.get(customerAccount, 'id');
  const rackspaceAccountNumber = _.get(customerAccount, 'domain');
  if (!awsAccountName || !awsAccountId || !rackspaceAccountNumber) {
    return buildPair('None');
  }

  return buildPair(
    <ExternalLink href={`${baseUrl()}/racker/rackspace-accounts/${rackspaceAccountNumber}/aws-accounts/${awsAccountId}`}>
      {awsAccountName}
    </ExternalLink>,
  );
};

export const useCurrentProject = () => {
  const projectId = useParsedQueryString()?.project;
  const projectsQuery = useQueryGetProjectsFromJanus();
  const project = _.find(projectsQuery?.data?.projects, { projectId });
  return project;
};

export const ProjectDetails = ({ showSystemsAccount, showManagementButtons }) => {
  const project = useCurrentProject();
  const [linkedState, setLinkedState] = React.useState(project?.linkedState);
  _.assign(project, { linkedState });
  const classes = 'd-grid grid-cols-sm-2 grid-cols-1';

  return (
    <Card className="border-0">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <Card.Title as="h4" className="mb-0">{project?.name}</Card.Title>
        <Route path="/racker/customers/:rackspaceAccountNumber">
          <div className="d-flex flex-wrap gap-4 flex-column flex-sm-row">
            <SmartGCPRequestAccessButton project={project} includeShell />
            <PassportButton project={project} />
          </div>
        </Route>
      </Card.Header>
      <Card.Body>
        <ListGroup variant="flush">
          <ListGroup.Item className={classes}>
            <Pair label="Linked State" value={<ProjectLinkedState project={project} />} />
          </ListGroup.Item>
          <ListGroup.Item className={classes}>
            <Pair label="Project Id" value={project?.projectId} />
          </ListGroup.Item>
          <ListGroup.Item className={classes}>
            <Pair label="Project Number" value={project?.projectNumber} />
          </ListGroup.Item>
          <ListGroup.Item className={classes}>
            <ServiceLevelPair label="Service Level" project={project} value={<ServiceLevel project={project} />} />
          </ListGroup.Item>
          <ListGroup.Item className={classes}>
            <Pair label="Created on" value={moment(project?.createdAt).format('MMMM Do YYYY, h:mm A')} />
          </ListGroup.Item>
          <ListGroup.Item className={classes}>
            <Pair label="Lifecycle State" value={_.startCase(_.kebabCase(project?.lifecycleState))} />
          </ListGroup.Item>
          {
            showSystemsAccount && (
              <ListGroup.Item className={classes}>
                <SystemsAccountPair projectId={project?.projectId} />
              </ListGroup.Item>
            )
          }
        </ListGroup>
      </Card.Body>
      {
        showManagementButtons && (
          <Card.Footer>
            <div className="d-flex justify-content-end mt-6 gap-4 px-0">
              <ManageServicesButton project={project} />
              <UnlinkModalButton project={project} onSuccess={() => setLinkedState('UNLINKED')} />
            </div>
          </Card.Footer>
        )
      }
    </Card>
  );
};
